import { fetcher } from '../../lib/api-fetcher';
import { useMemo } from 'react';
import { createFileRoute, useSearch, useNavigate } from '@tanstack/react-router';
import { defineSplitPage, useSplitPage } from '@repo/split';
import { defineListPage, type PaginatedQueryFn } from '@repo/list';
import { SplitPage } from '@repo/web-split';
import { createUrlAdapter } from '@repo/web-list';
import { usePageTitle } from '../../lib/hooks';
import { getSession } from '../../lib/session';
import type { PrizePayoutItem, PrizePayoutDetail, PayoutStatus } from '../../features/payouts/network/payouts.api';
import { PayoutDetailContent } from '../../features/payouts/components/PayoutDetailContent';
import { StatusBadge } from '../../components/ui/StatusBadge';


const STATUS_CONFIG: Record<PayoutStatus, { label: string; variant: 'warning' | 'info' | 'success' | 'danger' }> = {
  PENDING_SURETY:      { label: 'Waiting on Surety',   variant: 'warning' },
  PENDING_APPROVAL:    { label: 'Pending Approval',    variant: 'info' },
  APPROVED:            { label: 'Approved',             variant: 'success' },
  INSTRUCTION_CREATED: { label: 'Instruction Created', variant: 'success' },
  PAID:                { label: 'Paid',                 variant: 'success' },
  FAILED:              { label: 'Failed',               variant: 'danger' },
  CANCELLED:           { label: 'Cancelled',            variant: 'danger' },
  REVERSED:            { label: 'Reversed',             variant: 'danger' },
};

const fetchPayouts: PaginatedQueryFn<PrizePayoutItem> = async (params) => {
  const qs = new URLSearchParams({ page: params.page || '1', limit: params.limit || '20' });
  if (params.status) qs.append('status', params.status);
  return fetcher.get<{ data: PrizePayoutItem[]; meta: { total: number; page: number; limit: number } }>(
    `/staff/v1/dividends/prize-payouts?${qs}`,
  );
};

const fetchPayoutDetail = (id: string) =>
  fetcher.get<PrizePayoutDetail>(`/staff/v1/dividends/prize-payouts/${id}`);

import { PageHeader } from '../../components/layout/PageHeader';

export const Route = createFileRoute('/_auth/payouts')({
  component: PayoutsPage,
});

function PayoutsPage() {
  usePageTitle('Prize Payouts');
  const navigate = useNavigate();
  const search = useSearch({ strict: false });

  const session = getSession();
  const canRead = session?.permissions?.includes('chitfunds.payout.read') ?? false;

  const payoutsConfig = useMemo(
    () =>
      defineSplitPage<PrizePayoutItem, PrizePayoutDetail>({
        resource: 'payouts',
        listConfig: defineListPage<PrizePayoutItem>({
          variant: 'data-table',
          resource: 'payouts',
          columns: [
            {
              id: 'winnerSubscriptionId',
              header: 'Winner (Ticket ID)',
              accessor: (row) => (row.winnerSubscriptionId || '').slice(0, 8) + '…',
              renderType: 'code',
            },
            {
              id: 'netPayout',
              header: 'Net Prize',
              accessor: 'netPayout',
              renderType: 'money',
              numeric: true,
            },
            {
              id: 'status',
              header: 'Status',
              accessor: 'status',
            },
            {
              id: 'suretyClearedAt',
              header: 'Surety',
              accessor: (row) => (row.suretyClearedAt ? 'Cleared' : 'Pending'),
            },
            {
              id: 'instructionId',
              header: 'Instruction',
              accessor: (row) => (row.instructionId ? row.instructionId.slice(0, 8) + '…' : '—'),
            },
            {
              id: 'createdAt',
              header: 'Created',
              accessor: 'createdAt',
              renderType: 'date',
            },
          ],
          filters: [
            {
              id: 'status',
              label: 'Status',
              type: 'select',
              options: [
                { label: 'Waiting on Surety', value: 'PENDING_SURETY' },
                { label: 'Pending Approval', value: 'PENDING_APPROVAL' },
                { label: 'Approved', value: 'APPROVED' },
                { label: 'Instruction Created', value: 'INSTRUCTION_CREATED' },
                { label: 'Paid', value: 'PAID' },
              ],
            },
          ],
          onRowClick: () => {},
          defaultPageSize: 20,
        }),
        detailFetchById: fetchPayoutDetail,
      }),
    [],
  );

  const urlAdapter = useMemo(
    () => createUrlAdapter(search, navigate as never),
    [search, navigate],
  );

  const list = useSplitPage(payoutsConfig, fetchPayouts, urlAdapter);

  if (!canRead) {
    return (
      <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
        <PageHeader title="Prize Payouts" subtitle="All payout activity across schemes." />
        <div className="flex items-center justify-center h-48">
          <p className="text-sm text-text-muted">You do not have permission to view prize payouts.</p>
        </div>
      </div>
    );
  }

  return (
    <SplitPage
      config={payoutsConfig}
      list={list}
      title="Prize Payouts"
      subtitle="All payout activity across schemes."
      customRenderers={{
        status: (row) => {
          const s = row.status as PayoutStatus;
          const cfg = STATUS_CONFIG[s] ?? { label: s, variant: 'info' as const };
          return <StatusBadge status={cfg.variant} label={cfg.label} />;
        },
        suretyClearedAt: (row) =>
          row.suretyClearedAt ? (
            <StatusBadge status="success" label="Cleared" dot={false} />
          ) : (
            <StatusBadge status="warning" label="Pending" dot={false} />
          ),
      }}
      detailPane={(detail) => <PayoutDetailContent payout={detail} />}
    />
  );
}